import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, forwardRef, Input, NgModule, OnDestroy, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { Subscription } from 'rxjs';

interface EquipmentTypeOption {
  key: string;
  value: string;
}

@Component({
  selector: 'equipment-type-combobox',
  templateUrl: './equipment-type-combobox.component.html',
  styleUrls: ['./equipment-type-combobox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EquipmentTypeComboboxComponent),
      multi: true
    }
  ]
})
export class EquipmentTypeComboboxComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() id = 'equipmentType';
  @Input() name = 'equipmentType';
  @Input() placeholder = 'Select equipment type';
  @Input() inputClass = 'input mt-1';
  @Input() loading = false;

  @Input() set equipmentTypes(types: EquipmentTypeOption[]) {
    this.sortedEquipmentTypes = [...(types || [])]
      .filter(type => type?.key && type?.value)
      .sort((a, b) => a.value.localeCompare(b.value));

    this.syncDisplayWithValue();
    this.filterEquipmentTypes(this.displayControl.value || '');
  }

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

  displayControl = new FormControl('');
  filteredEquipmentTypes: EquipmentTypeOption[] = [];

  private sortedEquipmentTypes: EquipmentTypeOption[] = [];
  private selectedKey: string;
  private selectedLabel = '';
  private activeSearchText = '';
  private valueChangesSub: Subscription;
  private scrollListener: () => void;
  private suppressOpen = false;
  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  constructor() {
    this.valueChangesSub = this.displayControl.valueChanges.subscribe(value => {
      const searchText = this.displayEquipmentType(value);
      this.filterEquipmentTypes(searchText);

      if (this.selectedLabel && searchText !== this.selectedLabel) {
        this.selectedKey = null;
        this.selectedLabel = '';
        this.onChange(null);
      }
    });
  }

  ngAfterViewInit(): void {
    this.scrollListener = () => this.updatePanelPosition();
    window.addEventListener('scroll', this.scrollListener, true);
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
    }
  }

  writeValue(value: string): void {
    this.selectedKey = value;
    this.syncDisplayWithValue();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.displayControl.disable({ emitEvent: false });
      return;
    }

    this.displayControl.enable({ emitEvent: false });
  }

  displayEquipmentType = (equipmentType: EquipmentTypeOption | string): string => {
    return typeof equipmentType === 'string' ? equipmentType : equipmentType?.value || '';
  };

  openPanel(): void {
    if (this.suppressOpen) return;

    const displayValue = this.displayEquipmentType(this.displayControl.value);
    const searchText = displayValue === this.selectedLabel ? '' : displayValue;

    this.filterEquipmentTypes(searchText);
    setTimeout(() => {
      this.autocompleteTrigger?.openPanel();
      this.updatePanelPosition();
    });
  }

  selectEquipment(event: MatAutocompleteSelectedEvent): void {
    this.setSelectedEquipment(event.option.value as EquipmentTypeOption);
  }

  handleTabSelection(): void {
    if (!this.autocompleteTrigger?.panelOpen) return;

    const activeOption = this.autocompleteTrigger.activeOption;
    if (!activeOption || activeOption.disabled) return;

    this.setSelectedEquipment(activeOption.value as EquipmentTypeOption);
  }

  private setSelectedEquipment(equipmentType: EquipmentTypeOption): void {
    this.selectedKey = equipmentType.key;
    this.selectedLabel = equipmentType.value;
    this.displayControl.setValue(equipmentType.value, { emitEvent: false });
    this.onChange(equipmentType.key);
    this.onTouched();
    this.suppressOpen = true;
    this.autocompleteTrigger?.closePanel();
    setTimeout(() => this.suppressOpen = false, 200);
  }

  handleBlur(): void {
    this.onTouched();

    const typedValue = this.displayEquipmentType(this.displayControl.value);
    const exactMatch = this.sortedEquipmentTypes.find(type => type.value.toLowerCase() === typedValue.toLowerCase());

    if (exactMatch) {
      this.selectedKey = exactMatch.key;
      this.selectedLabel = exactMatch.value;
      this.displayControl.setValue(exactMatch.value, { emitEvent: false });
      this.onChange(exactMatch.key);
      return;
    }

    if (!this.selectedKey || typedValue !== this.selectedLabel) {
      this.selectedKey = null;
      this.selectedLabel = '';
      this.displayControl.setValue('', { emitEvent: false });
      this.onChange(null);
    }
  }

  highlightMatch(value: string): string {
    const searchText = this.activeSearchText;
    const matchIndex = value.toLowerCase().indexOf(searchText.toLowerCase());

    if (!searchText || matchIndex === -1) {
      return this.highlightMatchingTokens(value, searchText);
    }

    const beforeMatch = value.slice(0, matchIndex);
    const match = value.slice(matchIndex, matchIndex + searchText.length);
    const afterMatch = value.slice(matchIndex + searchText.length);

    return `${this.escapeHtml(beforeMatch)}<mark>${this.escapeHtml(match)}</mark>${this.escapeHtml(afterMatch)}`;
  }

  private filterEquipmentTypes(searchText: string): void {
    this.activeSearchText = searchText;
    const normalizedSearchText = this.normalizeSearchText(searchText);
    const searchTokens = this.normalizeSearchTokens(searchText);

    this.filteredEquipmentTypes = this.sortedEquipmentTypes.filter(type => {
      const normalizedLabel = this.normalizeSearchText(type.value);
      const labelTokens = this.normalizeSearchTokens(type.value);
      return !normalizedSearchText ||
        type.value.toLowerCase().includes(searchText.toLowerCase()) ||
        normalizedLabel.includes(normalizedSearchText) ||
        this.matchesAllSearchTokens(labelTokens, searchTokens);
    });
  }

  private updatePanelPosition(): void {
    if (this.autocompleteTrigger?.panelOpen) {
      this.autocompleteTrigger.updatePosition();
    }
  }

  private syncDisplayWithValue(): void {
    const selectedEquipmentType = this.findEquipmentType(this.selectedKey);
    this.selectedLabel = selectedEquipmentType?.value || '';
    this.displayControl.setValue(this.selectedLabel || this.selectedKey || '', { emitEvent: false });
  }

  private findEquipmentType(value: string): EquipmentTypeOption {
    if (!value) return null;

    return this.sortedEquipmentTypes.find(type =>
      type.key === value ||
      type.value.toLowerCase() === value.toLowerCase()
    );
  }

  private normalizeSearchText(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/\bwith\b/g, 'w')
      .replace(/\btrks?\b/g, 'truck')
      .replace(/\btrlr\b/g, 'trailer')
      .replace(/\bfb\b/g, 'flatbed')
      .replace(/\bw\b/g, 'w')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  private normalizeSearchTokens(value: string): string[] {
    return (value || '')
      .toLowerCase()
      .replace(/\bwith\b/g, 'w')
      .replace(/\btrks?\b/g, 'truck')
      .replace(/\btrlr\b/g, 'trailer')
      .replace(/\bfb\b/g, 'flatbed')
      .replace(/w\//g, 'w ')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  }

  private matchesAllSearchTokens(labelTokens: string[], searchTokens: string[]): boolean {
    if (!searchTokens.length) return true;

    return searchTokens.every(searchToken =>
      labelTokens.some(labelToken =>
        labelToken.includes(searchToken)
      )
    );
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private highlightMatchingTokens(value: string, searchText: string): string {
    const searchTokens = this.normalizeSearchTokens(searchText);
    if (!searchTokens.length) return this.escapeHtml(value);

    const ranges = this.getMatchingTokenRanges(value, searchTokens);
    if (!ranges.length) return this.escapeHtml(value);

    return this.renderHighlightedRanges(value, ranges);
  }

  private getMatchingTokenRanges(value: string, searchTokens: string[]): Array<{ start: number, end: number }> {
    const ranges = [];
    const tokenRegex = /[a-z0-9]+/gi;
    let match: RegExpExecArray;

    while ((match = tokenRegex.exec(value)) !== null) {
      const rawLabelToken = match[0].toLowerCase();
      const normalizedLabelToken = this.normalizeSearchTokens(match[0])[0];
      const matchedSearchToken = searchTokens.find(searchToken =>
        normalizedLabelToken.includes(searchToken)
      );

      if (matchedSearchToken) {
        const matchStart = rawLabelToken.indexOf(matchedSearchToken);
        const start = match.index + matchStart;
        const end = start + matchedSearchToken.length;

        ranges.push({
          start,
          end
        });
      }
    }

    return this.mergeHighlightRanges(ranges);
  }

  private mergeHighlightRanges(ranges: Array<{ start: number, end: number }>): Array<{ start: number, end: number }> {
    return ranges
      .sort((a, b) => a.start - b.start)
      .reduce((mergedRanges, range) => {
        const previousRange = mergedRanges[mergedRanges.length - 1];

        if (previousRange && range.start <= previousRange.end) {
          previousRange.end = Math.max(previousRange.end, range.end);
          return mergedRanges;
        }

        mergedRanges.push({ ...range });
        return mergedRanges;
      }, []);
  }

  private renderHighlightedRanges(value: string, ranges: Array<{ start: number, end: number }>): string {
    let highlightedValue = '';
    let currentIndex = 0;

    ranges.forEach(range => {
      highlightedValue += this.escapeHtml(value.slice(currentIndex, range.start));
      highlightedValue += `<mark>${this.escapeHtml(value.slice(range.start, range.end))}</mark>`;
      currentIndex = range.end;
    });

    highlightedValue += this.escapeHtml(value.slice(currentIndex));
    return highlightedValue;
  }
}

@NgModule({
  declarations: [EquipmentTypeComboboxComponent],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule
  ],
  exports: [EquipmentTypeComboboxComponent]
})
export class EquipmentTypeComboboxModule { }
